import moment from 'moment';
import { IProperty } from 'features/property';
import { ISavelist } from 'features/savelistApi/typings';
import { getInflationPrice } from 'utils/common';

/**
 * Sanitize string cell values to prevent Excel formula injection.
 * Prefixes dangerous characters with a single quote.
 */
function sanitizeCell(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  if (/^[=+\-@\t\r]/.test(value)) return `'${value}`;
  return value;
}

/**
 * Convert a value to a cell-safe representation.
 * Returns empty string for null/undefined/NaN.
 * Returns 0 as empty for constructionYear and fireInsuranceTotalValue.
 */
function toCell(value: unknown, treatZeroAsEmpty = false): unknown {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && Number.isNaN(value)) return '';
  if (treatZeroAsEmpty && value === 0) return '';
  return sanitizeCell(value);
}

/**
 * Format a unix timestamp to DD/MM/YYYY string.
 */
function formatSoldDate(recentDate: number | undefined | null): string {
  if (!recentDate) return '';
  return moment.unix(recentDate).format('DD/MM/YYYY');
}

/**
 * Calculate the display price for a property (matches Card.tsx getPrice logic).
 * If available for sale, use recentPrice. Otherwise, use inflation-adjusted price.
 */
function getPropertyPrice(property: IProperty): number | '' {
  if (!property.recentPrice && !property.inflatedPrice) return '';
  if (property.availableForSale) {
    return property.recentPrice || '';
  }
  const inflated = property.inflatedPrice
    ? property.inflatedPrice
    : getInflationPrice({
      year: property.recentDate
        ? moment.unix(property.recentDate).format('YYYY.MM').replace('.', 'M')
        : '',
      price: property.recentPrice,
      type: property.propertyType,
      postcode: property.postcode,
    });
  return inflated || '';
}

/**
 * Calculate the display price for the savelist's own property.
 * If editedPricePerSqMeter > 0, use that. Otherwise, use estimatedPricePerSqMeter.
 */
function getSavelistPrice(savelist: ISavelist): number | '' {
  const pricePerSqm = savelist.editedPricePerSqMeter > 0
    ? savelist.editedPricePerSqMeter
    : savelist.estimatedPricePerSqMeter;
  if (!pricePerSqm || !savelist.squareMeters) return '';
  return Math.round(pricePerSqm * savelist.squareMeters);
}

/**
 * Build a row object from a savelist (first row in Excel).
 */
function buildSavelistRow(savelist: ISavelist): Record<string, unknown> {
  const price = getSavelistPrice(savelist);
  const pricePerSqm = savelist.editedPricePerSqMeter > 0
    ? savelist.editedPricePerSqMeter
    : savelist.estimatedPricePerSqMeter;

  return {
    Fasteignanúmer: toCell(savelist.propertyId),
    Heimilisfang: toCell(savelist.addressTitle),
    Póstnúmer: toCell(savelist.postcode),
    Tegund: toCell(savelist.propertyType),
    'Verð (kr)': toCell(price),
    'Verð/m² (kr)': toCell(pricePerSqm),
    Herbergi: toCell(savelist.rooms),
    Svefnherbergi: toCell(savelist.bedrooms),
    Baðherbergi: toCell(savelist.bathrooms),
    'Stærð (m²)': toCell(savelist.squareMeters),
    'Fasteignamat (kr)': toCell(savelist.nextYearValuationValue),
    Byggingarár: toCell(savelist.constructionYear, true),
    Selt: '',
  };
}

/**
 * Build a row object from a property (related properties).
 */
function buildPropertyRow(property: IProperty): Record<string, unknown> {
  const price = getPropertyPrice(property);
  const pricePerSqm = price && property.squareMeters
    ? (price as number) / property.squareMeters
    : '';

  return {
    Fasteignanúmer: toCell(property.id),
    Heimilisfang: toCell(property.title),
    Póstnúmer: toCell(property.postcode),
    Tegund: toCell(property.propertyType),
    'Verð (kr)': toCell(price),
    'Verð/m² (kr)': toCell(pricePerSqm),
    Herbergi: toCell(property.rooms),
    Svefnherbergi: toCell(property.bedrooms),
    Baðherbergi: toCell(property.bathrooms),
    'Stærð (m²)': toCell(property.squareMeters),
    'Fasteignamat (kr)': toCell(property.nextYearValuationValue),
    Byggingarár: toCell(property.constructionYear, true),
    Selt: formatSoldDate(property.recentDate),
  };
}

/**
 * Sanitize a string to be safe as a filename.
 */
function sanitizeFilename(name: string): string {
  return name
    .replace(/[/\\:*?"<>|]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Export savelist data to an Excel file and trigger browser download.
 *
 * @param XLSX - The dynamically imported xlsx module
 * @param savelist - The savelist data (first row)
 * @param properties - The related properties (remaining rows)
 */
export async function exportToExcel(
  XLSX: typeof import('xlsx'),
  savelist: ISavelist,
  properties: IProperty[],
): Promise<void> {
  const rows = [
    buildSavelistRow(savelist),
    ...properties.map(buildPropertyRow),
  ];

  const worksheet = XLSX.utils.json_to_sheet(rows);

  // Set column widths for readability
  worksheet['!cols'] = [
    { wch: 14 },
    { wch: 25 },
    { wch: 12 },
    { wch: 16 },
    { wch: 18 },
    { wch: 16 },
    { wch: 10 },
    { wch: 14 },
    { wch: 12 },
    { wch: 12 },
    { wch: 18 },
    { wch: 12 },
    { wch: 14 },
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Eignir');

  const filename = `${sanitizeFilename(savelist.name || 'savelist')}_${moment().format('YYYY-MM-DD')}.xlsx`;
  XLSX.writeFile(workbook, filename);
}
